import type { Path } from 'react-hook-form';
import { AddButton } from '#components/atoms/buttons/add-button/AddButton.tsx';
import { BackgroundCard } from '#components/atoms/cards/background-card/BackgroundCard.tsx';
import { DividedList } from '#components/atoms/lists/divided-list/DividedList.tsx';
import { TagLink } from '#components/patterns/entity-links/tag-link/TagLink.tsx';
import { MeatballMenu, MenuItem } from '#components/patterns/menus/meatball-menu/index.ts';
import type { CIDRRule, LocalAllowRule } from '#dn-types/config-override.ts';
import { ResetButton } from '#screens/hosts/form/advanced/reset-button/ResetButton.tsx';
import type { OverridePlusSource } from '../../../hooks/useOverrideMaps.ts';
import type { HostFormData } from '../../host-form-schema.ts';
import { AllowRule, type AllowListItem } from './AllowRule.tsx';
import { checkIsAllowListFromTag } from './checkIsAllowListFromTag.ts';
import styles from './AllowList.module.css';

export type RemoteResetArgs = CIDRRule[];
export type LocalResetArgs = LocalAllowRule[];

type Props<T extends Path<HostFormData>> = {
  name: T;
  items: AllowListItem[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  onReset: (values: T extends 'configOverrides.lighthouse.local_allow_list' ? LocalResetArgs : RemoteResetArgs) => void;
  addButtonProps?: Omit<
    React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
    'ref'
  >;
  /** A ref to pass down to the + Add button, so we can focus it on error */
  addButtonRef?: React.Ref<HTMLButtonElement>;
  overrideMap: Map<string, OverridePlusSource>;
};

export function AllowList<
  const T extends 'configOverrides.lighthouse.local_allow_list' | 'configOverrides.lighthouse.remote_allow_list',
>({ name, onAdd, onRemove, onEdit, onReset, addButtonProps, items, addButtonRef, overrideMap }: Props<T>) {
  const { tagName, isValueFromTag, tagProvidedValue } = checkIsAllowListFromTag({ name, items, overrideMap });

  return (
    <div className="mb-24">
      {tagName && isValueFromTag ?
        <BackgroundCard className="mb-12">
          <ul className="list-inline flex flex-col gap-4 mb-12">
            {items.map((item) => (
              <li className="p-md flex justify-between align-center" key={item.id}>
                <AllowRule item={item} />
              </li>
            ))}
          </ul>
          <span className="p-sm">
            Inherited from <TagLink className="a" tagName={tagName} />
          </span>
        </BackgroundCard>
      : <DividedList outsideBorder="both" className="mb-12">
          {items.map((item, index) => (
            <li className="flex justify-between align-center" key={item.id}>
              <AllowRule item={item} />
              <MeatballMenu>
                <MenuItem
                  onClick={() => {
                    onEdit(index);
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onRemove(index);
                  }}
                >
                  Delete
                </MenuItem>
              </MeatballMenu>
            </li>
          ))}
        </DividedList>
      }

      <div className={styles.AllowList_footer}>
        <AddButton className="weight-med" onClick={onAdd} {...addButtonProps} ref={addButtonRef} />
        {tagProvidedValue && !isValueFromTag ?
          <ResetButton onClick={() => onReset(tagProvidedValue)} isShown={true} tagName={tagName} />
        : null}
      </div>
    </div>
  );
}
